import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";

interface CheckinEvent {
  id: number;
  title: string;
  event_datetime: string;
  location: string;
}

interface Registration {
  id: number;
  name: string;
  checked_in_at: string | null;
}

type Phase =
  | "loading"
  | "not_found"
  | "invalid_token"
  | "before"
  | "closed"
  | "ready"
  | "done";

export default function CheckinPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("t") || "";

  const [phase, setPhase] = useState<Phase>("loading");
  const [event, setEvent] = useState<CheckinEvent | null>(null);
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [query, setQuery] = useState("");
  const [doneName, setDoneName] = useState("");
  const [checkingInId, setCheckingInId] = useState<number | null>(null);
  const [checkinError, setCheckinError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setPhase("loading");
      const idNum = Number(eventId);
      if (!idNum || Number.isNaN(idNum)) {
        setPhase("not_found");
        return;
      }

      const { data, error } = await supabase.rpc("checkin_load", {
        p_event_id: idNum,
        p_token: token,
      });

      if (cancelled) return;

      if (error || !data) {
        setPhase("not_found");
        return;
      }

      if (data.status === "not_found") {
        setPhase("not_found");
        return;
      }
      if (data.status === "invalid_token") {
        setPhase("invalid_token");
        return;
      }
      if (data.status === "before") {
        setPhase("before");
        return;
      }
      if (data.status === "closed") {
        setPhase("closed");
        return;
      }

      setEvent(data.event || null);
      setRegistrations(data.registrations || []);
      setPhase("ready");
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [eventId, token]);

  async function checkIn(reg: Registration) {
    if (reg.checked_in_at) return;
    setCheckingInId(reg.id);
    setCheckinError("");
    const { data, error } = await supabase.rpc("checkin_mark", {
      p_event_id: Number(eventId),
      p_token: token,
      p_registration_id: reg.id,
    });
    setCheckingInId(null);

    if (error || !data) {
      setCheckinError("Não consegui registrar o check-in. Tente de novo.");
      return;
    }

    if (data.status === "ok") {
      setDoneName(data.name || reg.name);
      setPhase("done");
      return;
    }

    if (data.status === "invalid_token") {
      setPhase("invalid_token");
      return;
    }

    if (data.status === "fora_da_janela") {
      setPhase("closed");
      return;
    }

    setCheckinError("Não encontrei sua inscrição. Procure a recepção.");
  }

  const q = query.trim().toLowerCase();
  const showResults = q.length >= 3;
  const filtered = showResults
    ? registrations.filter((r) => (r.name || "").toLowerCase().includes(q))
    : [];

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-beige flex items-center justify-center px-6">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="font-body text-sm text-brown/60">Abrindo check-in...</p>
        </div>
      </div>
    );
  }

  if (phase === "not_found") {
    return (
      <Shell>
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-brown/10 flex items-center justify-center">
          <i className="ri-calendar-2-line text-2xl text-brown/40" />
        </div>
        <h1 className="font-display text-xl text-brown mb-2">Evento não encontrado</h1>
        <p className="font-body text-sm text-brown/60">
          Confira se o link está correto ou peça ajuda na recepção.
        </p>
      </Shell>
    );
  }

  if (phase === "invalid_token") {
    return (
      <Shell>
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-lock-line text-2xl text-red-500" />
        </div>
        <h1 className="font-display text-xl text-brown mb-2">Link inválido</h1>
        <p className="font-body text-sm text-brown/60">
          Este código de check-in não é mais válido. Peça um novo cartão na recepção.
        </p>
      </Shell>
    );
  }

  if (phase === "before") {
    return (
      <Shell>
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-amber-100 flex items-center justify-center">
          <i className="ri-time-line text-2xl text-amber-600" />
        </div>
        <h1 className="font-display text-xl text-brown mb-2">Ainda não abriu</h1>
        <p className="font-body text-sm text-brown/60">
          O check-in deste evento ainda não começou. Volte mais perto do horário.
        </p>
      </Shell>
    );
  }

  if (phase === "closed") {
    return (
      <Shell>
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-brown/10 flex items-center justify-center">
          <i className="ri-time-line text-2xl text-brown/40" />
        </div>
        <h1 className="font-display text-xl text-brown mb-2">Check-in encerrado</h1>
        <p className="font-body text-sm text-brown/60">
          O check-in deste evento já foi encerrado. Procure a recepção para se registrar.
        </p>
      </Shell>
    );
  }

  if (phase === "done") {
    return (
      <Shell>
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-green-100 flex items-center justify-center">
          <i className="ri-check-line text-3xl text-green-600" />
        </div>
        <h1 className="font-display text-xl text-brown mb-2">Check-in feito!</h1>
        <p className="font-body text-base text-brown/70">
          Até já, <strong className="text-brown">{doneName}</strong>!
        </p>
        <p className="font-body text-sm text-brown/50 mt-2">
          Pode entrar e aproveitar o encontro.
        </p>
      </Shell>
    );
  }

  // phase === "ready"
  return (
    <div className="min-h-screen bg-beige px-4 py-8 md:py-12">
      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-display text-xl md:text-2xl text-brown mb-2">
            Bem-vinda ao {event?.title}!
          </h1>
          <p className="font-body text-sm text-brown/60">
            Qual é o seu nome?
          </p>
        </div>

        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Digite seu nome..."
          autoFocus
          className="w-full px-5 py-4 rounded-2xl border-2 border-nude bg-white font-body text-lg text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
        />

        {checkinError && (
          <p className="mt-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 font-body text-sm text-red-700">
            {checkinError}
          </p>
        )}

        <div className="mt-6 space-y-3">
          {!showResults && (
            <p className="text-center font-body text-sm text-brown/50">
              Digite pelo menos 3 letras para encontrar seu nome.
            </p>
          )}

          {showResults && filtered.length === 0 && (
            <div className="text-center py-8">
              <p className="font-body text-sm text-brown/60">
                Não encontrei ninguém com esse nome na lista.
              </p>
              <p className="font-body text-xs text-brown/40 mt-1">
                Procure a recepção para confirmar sua inscrição.
              </p>
            </div>
          )}

          {showResults &&
            filtered.map((reg) => (
              <button
                key={reg.id}
                onClick={() => checkIn(reg)}
                disabled={!!reg.checked_in_at || checkingInId !== null}
                className={`w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border-2 text-left transition-colors cursor-pointer ${
                  reg.checked_in_at
                    ? "bg-green-50 border-green-200 opacity-70 cursor-default"
                    : "bg-white border-nude hover:border-primary"
                }`}
              >
                <span className="font-body text-lg text-brown">{reg.name}</span>
                {reg.checked_in_at ? (
                  <span className="flex items-center gap-1 text-green-600 font-body text-sm whitespace-nowrap">
                    <i className="ri-checkbox-circle-line" /> fez check-in
                  </span>
                ) : checkingInId === reg.id ? (
                  <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                ) : (
                  <i className="ri-arrow-right-line text-brown/40" />
                )}
              </button>
            ))}
        </div>
      </div>
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-beige flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-white rounded-2xl p-8 md:p-10 text-center">
        {children}
      </div>
    </div>
  );
}